import { useEffect, useMemo } from "react";

// The count's clock and the ONE reading of it (#326). The engine
// (useCountTimer) keeps the clock and reads it only at the instants the logic
// needs (the 3-2-1 and zero); the components that show the seconds
// (useTimerDisplay) read the same clock once per shown second. Both go through
// readClock, so what "zero", "remaining" and "overtime" mean has one
// definition: a rounding rule changed on one side only would show a number the
// cues do not agree with.
//
// Time is wall-clock based (Date.now), so the count survives tab/app
// backgrounding: down reads ceil((endsAt - now)/1000), up reads
// floor((now - startedAt)/1000).

export type TimerMode = "down" | "up";

/** What a count IS, as opposed to what it reads at a given instant. Changes
 *  only on an action (start, ±N, pause, resume, reset), never with time. */
export type TimerClock = {
  mode: TimerMode;
  // down: wall-clock deadline; null when idle/paused. up: always null.
  endsAt: number | null;
  // up: wall-clock start; null when idle/paused. down: the start instant of a
  // countdown started with `overtime`, null otherwise.
  startedAt: number | null;
  // down: the duration the countdown started from. up: the optional target
  // (0 = open-ended stopwatch).
  totalSec: number;
  // down: captured remaining; up: captured elapsed. Non-null === paused.
  pausedSec: number | null;
  // Overtime countdowns run TWO clocks (a deadline for the cue, a start
  // instant for the measure), so pausing one has to capture both. Null for
  // every non-overtime timer.
  pausedElapsedSec: number | null;
};

/** A count that is not running: what every timer reads before start(). */
export const IDLE_CLOCK: TimerClock = {
  mode: "down",
  endsAt: null,
  startedAt: null,
  totalSec: 0,
  pausedSec: null,
  pausedElapsedSec: null,
};

/** The clock picked out of any state that carries it, as a value whose
 *  identity changes only when the count does. The one place the six fields
 *  are listed as deps: a readout keys its effects on it, and an adapter can
 *  memoize on it without naming the fields again. */
export function useClockOf(s: TimerClock): TimerClock {
  const { mode, endsAt, startedAt, totalSec, pausedSec, pausedElapsedSec } = s;
  return useMemo(
    () => ({ mode, endsAt, startedAt, totalSec, pausedSec, pausedElapsedSec }),
    [mode, endsAt, startedAt, totalSec, pausedSec, pausedElapsedSec],
  );
}

export type TimerReading = {
  // down: whole seconds left (clamped at 0). up: 0.
  remainingSec: number;
  // up: whole seconds counted so far. down: totalSec - remainingSec, UNLESS
  // the countdown was started with `overtime`, in which case it is the real
  // time held and keeps growing past totalSec (see isOvertime).
  elapsedSec: number;
  // The number a readout shows: remaining for down, elapsed for up. An
  // overtime countdown past its deadline shows the OVERSHOOT (the caller
  // renders it with a leading +), not a frozen 0.
  displaySec: number;
  // An overtime countdown that has passed its deadline and is still counting.
  // Always false for a plain countdown and for count-up.
  isOvertime: boolean;
  // The deadline has arrived and the clock is not held at it by a pause. Two
  // consumers watch for it to fire once (the rest timer's grace window, the
  // prescribed bout's self-completion); one changed definition of "zero"
  // would otherwise land in one of them and not the other, silently.
  atZero: boolean;
};

// Whole seconds run since a wall-clock instant, and its inverse: the instant a
// count that already holds `heldSec` must have started from. Both directions
// live here because pause/resume use them in pairs (capture the seconds, then
// rebuild the instant), and a rounding rule changed on one side only would make
// a paused timer resume a second off.
export const elapsedSince = (startedAt: number, nowMs: number): number =>
  Math.max(0, Math.floor((nowMs - startedAt) / 1000));
export const startedAtFor = (nowMs: number, heldSec: number): number =>
  nowMs - heldSec * 1000;
// Whole seconds left before a deadline, clamped at 0.
export const remainingUntil = (endsAt: number, nowMs: number): number =>
  Math.ceil(Math.max(0, endsAt - nowMs) / 1000);

export function readClock(c: TimerClock, nowMs: number): TimerReading {
  const runningRemainingSec =
    c.endsAt === null ? 0 : remainingUntil(c.endsAt, nowMs);
  const runningElapsedSec =
    c.startedAt === null ? 0 : elapsedSince(c.startedAt, nowMs);
  const remainingSec =
    c.mode === "down"
      ? c.pausedSec !== null
        ? c.pausedSec
        : runningRemainingSec
      : 0;
  // A count-down carries a startedAt ONLY when it was started with overtime,
  // so this flag needs no state of its own: the second clock IS the flag.
  const measuresRealTime = c.startedAt !== null || c.pausedElapsedSec !== null;
  const elapsedSec =
    c.mode === "up"
      ? c.pausedSec !== null
        ? c.pausedSec
        : runningElapsedSec
      : measuresRealTime
        ? c.pausedElapsedSec !== null
          ? c.pausedElapsedSec
          : runningElapsedSec
        : // Plain countdown: capped at totalSec by construction.
          Math.max(0, c.totalSec - remainingSec);
  // Overtime = the deadline is behind us AND we actually overshot it. Both
  // halves earn their place: without `remainingSec === 0` a +10 taken while
  // overrunning would keep the + on a dial that has seconds left to run again,
  // and without the overshoot check the dial would flash "+0:00" at the exact
  // moment it hits zero.
  const isOvertime =
    c.mode === "down" &&
    measuresRealTime &&
    remainingSec === 0 &&
    elapsedSec > c.totalSec;
  const atZero = c.endsAt !== null && remainingSec <= 0 && c.pausedSec === null;
  const displaySec =
    c.mode === "down"
      ? isOvertime
        ? elapsedSec - c.totalSec
        : remainingSec
      : elapsedSec;
  return { remainingSec, elapsedSec, displaySec, isOvertime, atZero };
}

// The cued last seconds of a count-down (usePhaseCues' 3-2-1). The engine
// wakes on each of them, then on zero, and at no other instant (#325, #326).
export const LEAD_IN_SEC = 3;
export const nextCueAt = (endsAt: number, nowMs: number): number | null => {
  for (let sec = LEAD_IN_SEC; sec >= 0; sec -= 1) {
    const at = endsAt - sec * 1000;
    if (at > nowMs) return at;
  }
  return null;
};

/** The next instant the shown second changes, or null when it never will (a
 *  paused or idle count, a plain countdown at zero). Down: the remaining drops
 *  at endsAt - k*1000. Up, and an overtime countdown past its deadline: the
 *  count moves at startedAt + (k+1)*1000. */
export function nextDisplayChangeAt(
  c: TimerClock,
  nowMs: number,
): number | null {
  if (c.pausedSec !== null) return null;
  if (c.mode === "down") {
    if (c.endsAt === null) return null;
    if (c.endsAt > nowMs) {
      const left = remainingUntil(c.endsAt, nowMs);
      return c.endsAt - (left - 1) * 1000;
    }
  }
  if (c.startedAt === null) return null;
  return c.startedAt + (elapsedSince(c.startedAt, nowMs) + 1) * 1000;
}

const MAX_WAKE_DELAY_MS = 5000;

/** One timeout aimed at `wakeAt`, handing the wall clock to `onWake` once it
 *  has really arrived. A timer can fire early (a clamped delay, a clock that
 *  moved), so the wake checks Date.now() and re-aims instead of trusting the
 *  delay. Null arms nothing. `onWake` is expected to be a state setter. */
export function useWakeAt(
  wakeAt: number | null,
  onWake: (nowMs: number) => void,
): void {
  useEffect(() => {
    if (wakeAt === null) return undefined;
    let id: ReturnType<typeof setTimeout> | undefined;
    // Capped: a timeout runs on monotonic time while wakeAt is wall-clock, so
    // a system clock jumped forward is caught within MAX_WAKE_DELAY_MS.
    const aim = (t: number) =>
      setTimeout(wake, Math.min(MAX_WAKE_DELAY_MS, Math.max(0, wakeAt - t)));
    const wake = () => {
      const t = Date.now();
      if (t < wakeAt) id = aim(t);
      else onWake(t);
    };
    id = aim(Date.now());
    return () => clearTimeout(id);
  }, [wakeAt, onWake]);
}
